import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Check, X, Globe2, Accessibility, GraduationCap, Heart } from "lucide-react";

const STORAGE_KEY = "onboarding_dismissed_v1";

const STEPS = [
  {
    id: 1,
    emoji: "👋",
    title: "Bienvenue sur Student Hub",
    subtitle: "On va simplifier ta vie étudiante en France.",
    body: "Une seule appli pour tes démarches CAF, ton logement CROUS, ton job, tes documents, ta communauté. Tu vas gagner des heures.",
    badge: "Made in France 🇫🇷",
  },
  {
    id: 2,
    emoji: "🎯",
    title: "Quel est ton profil ?",
    subtitle: "Pour personnaliser ton expérience.",
    profiles: [
      { id: "fr", icon: GraduationCap, label: "Étudiant·e en France", desc: "Démarches, logement, jobs, IA", color: "from-blue-500 to-violet-500" },
      { id: "intl", icon: Globe2, label: "International / Erasmus", desc: "Visa, ANEF, CAF + traduction 8 langues", color: "from-emerald-500 to-blue-500" },
      { id: "handicap", icon: Accessibility, label: "Besoins spécifiques", desc: "MDPH, aménagements, accompagnement", color: "from-violet-500 to-pink-500" },
      { id: "wellbeing", icon: Heart, label: "J'ai besoin de soutien", desc: "Aide psy, écoute, bien-être", color: "from-rose-500 to-orange-500" },
    ],
  },
  {
    id: 3,
    emoji: "✨",
    title: "Tu es prêt·e !",
    subtitle: "Pour bien démarrer, voici ce qu'on te suggère :",
    suggestions: [
      "Configure ton profil dans Paramètres",
      "Active la 2FA pour plus de sécurité",
      "Coche tes premières démarches",
      "Rejoins la communauté (chat anonyme)",
    ],
  },
];

const PROFILE_ROUTES = {
  fr: "/dashboard",
  intl: "/etudiants-internationaux",
  handicap: "/accessibilite",
  wellbeing: "/bien-etre",
};

export default function OnboardingModal() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setOpen(true);
    } catch { /* ignore */ }
  }, []);

  const close = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore quota */ }
    setOpen(false);
  };

  const next = () => {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
    else close();
  };

  if (!open) return null;

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300"
      role="dialog"
      aria-labelledby="onboarding-title"
      data-testid="onboarding-modal"
    >
      <div className="relative w-full max-w-lg rounded-3xl bg-white shadow-2xl overflow-hidden animate-in slide-in-from-bottom-6 duration-300">
        {/* Top progress dots */}
        <div className="absolute top-5 left-5 flex items-center gap-1.5" aria-hidden="true">
          {STEPS.map((s, i) => (
            <span
              key={s.id}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? "w-6 bg-violet-600" : i < step ? "w-1.5 bg-emerald-500" : "w-1.5 bg-slate-200"
              }`}
            />
          ))}
        </div>
        <button
          onClick={close}
          className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700"
          aria-label="Fermer l'onboarding"
          data-testid="onboarding-close"
        >
          <X size={16} />
        </button>

        <div className="p-8 sm:p-10 pt-14">
          <div className="text-5xl mb-4" aria-hidden="true">{current.emoji}</div>
          <h2 id="onboarding-title" className="font-display text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
            {current.title}
          </h2>
          {current.subtitle && <p className="text-slate-600 mt-2">{current.subtitle}</p>}

          {/* Step 1 — welcome */}
          {step === 0 && (
            <div className="mt-5 space-y-3">
              <p className="text-slate-700 leading-relaxed">{current.body}</p>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-50 border border-violet-200 text-xs font-semibold text-violet-700">
                <Sparkles size={11} /> {current.badge}
              </span>
            </div>
          )}

          {/* Step 2 — profile picker */}
          {step === 1 && (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="onboarding-profiles">
              {current.profiles.map((p) => {
                const Icon = p.icon;
                const selected = profile === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setProfile(p.id)}
                    className={`text-left rounded-2xl border-2 p-3.5 transition-all ${
                      selected
                        ? "border-violet-500 bg-violet-50/50 shadow-md"
                        : "border-slate-200 bg-white hover:border-violet-300"
                    }`}
                    data-testid={`onboarding-profile-${p.id}`}
                    aria-pressed={selected}
                  >
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${p.color} text-white flex items-center justify-center mb-2 shadow-md`}>
                      <Icon size={18} />
                    </div>
                    <p className="font-semibold text-slate-900 text-sm">{p.label}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{p.desc}</p>
                  </button>
                );
              })}
            </div>
          )}

          {/* Step 3 — final */}
          {step === 2 && (
            <ul className="mt-5 space-y-2" data-testid="onboarding-suggestions">
              {current.suggestions.map((s) => (
                <li key={s} className="flex items-start gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={12} />
                  </span>
                  <span className="text-sm text-slate-700 leading-snug">{s}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Actions */}
          <div className="mt-7 flex flex-wrap items-center justify-between gap-3">
            <button
              onClick={close}
              className="text-xs text-slate-500 hover:text-slate-900 underline"
              data-testid="onboarding-skip"
            >
              Passer
            </button>
            {isLast && profile ? (
              <Link
                to={PROFILE_ROUTES[profile] || "/dashboard"}
                onClick={close}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white font-semibold text-sm shadow-md transition-all"
                data-testid="onboarding-go-profile"
              >
                Démarrer mon parcours <ArrowRight size={14} />
              </Link>
            ) : (
              <button
                onClick={next}
                disabled={step === 1 && !profile}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors"
                data-testid="onboarding-next"
              >
                {isLast ? "Terminer" : "Continuer"} <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
